import { useState } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Search, Phone, Mail, MapPin, FileText, Briefcase, User, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLES = {
  residential: "border-emerald-300 bg-emerald-50 text-emerald-800",
  commercial: "border-sky-300 bg-sky-50 text-sky-800",
  government: "border-violet-300 bg-violet-50 text-violet-800",
  rural: "border-amber-300 bg-amber-50 text-amber-800",
  other: "border-slate-300 bg-slate-50 text-slate-700",
};

export default function Clients() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [clientType, setClientType] = useState("all");
  const [delTarget, setDelTarget] = useState(null);

  const { data: types = [] } = useQuery({
    queryKey: ["client-types"],
    queryFn: async () => (await api.get("/clients/client-types")).data,
  });

  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (clientType && clientType !== "all") params.set("client_type", clientType);

  const { data: clients = [], isFetching } = useQuery({
    queryKey: ["clients", search, clientType],
    queryFn: async () => (await api.get(`/clients?${params.toString()}`)).data,
  });

  const deleteClient = useMutation({
    mutationFn: async (cid) => (await api.delete(`/clients/${cid}?cascade=true`)).data,
    onSuccess: () => {
      toast.success("Client and all linked records deleted");
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setDelTarget(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  return (
    <div>
      <PageHeader
        title="Client Database"
        subtitle="Every client. Every quote. Every job. All in one place."
        breadcrumb={["Clients", "Directory"]}
        actions={
          <Button data-testid="new-enquiry-btn" onClick={() => navigate("/enquiry/new")} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-10">
            <Plus size={14} className="mr-1.5" /> New Enquiry
          </Button>
        }
      />

      <div className="px-6 lg:px-10 py-6 space-y-4">
        {/* Search + filter */}
        <Card className="rounded-sm p-4 industrial-shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <div className="stamp-label text-slate-600 mb-1.5">Search</div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input
                  data-testid="client-search-input"
                  placeholder="Business, customer #, contact, email, phone, address…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-sm h-10 pl-9"
                />
              </div>
            </div>
            <div>
              <div className="stamp-label text-slate-600 mb-1.5">Client type</div>
              <Select value={clientType} onValueChange={setClientType}>
                <SelectTrigger data-testid="client-type-filter" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <DefaultViewControl screen="clients" className="h-10"
                current={{ type: clientType }}
                onApply={(f) => { if (f.type) setClientType(f.type); }} />
              <div className="text-xs text-slate-500 stamp-label">
                {isFetching ? "Loading…" : `${clients.length} ${clients.length === 1 ? "client" : "clients"}`}
              </div>
            </div>
          </div>
        </Card>

        {/* Grid of client cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((c) => {
            const pc = c.primary_contact;
            const addr = (c.addresses && c.addresses[0]) || null;
            return (
              <Card
                key={c.id}
                data-testid={`client-card-${c.id}`}
                onClick={() => navigate(`/clients/${c.id}`)}
                className="rounded-sm industrial-shadow overflow-hidden cursor-pointer hover:border-cyan-400 transition-colors"
              >
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                      <Building2 className="text-slate-500" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-slate-900 truncate">{c.name}</div>
                      {c.customer_number && <div className="text-[11px] text-cyan-600 font-mono font-semibold mt-0.5" data-testid={`client-number-${c.id}`}>{c.customer_number}</div>}
                      {c.abn_acn && <div className="text-[10px] text-slate-500 font-mono mt-0.5">ABN {c.abn_acn}</div>}
                      {c.client_type && (
                        <Badge variant="outline" className={`mt-1.5 rounded-sm text-[10px] ${TYPE_STYLES[c.client_type] || TYPE_STYLES.other}`}>
                          {c.client_type}
                        </Badge>
                      )}
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      data-testid={`delete-client-card-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); setDelTarget(c); }}
                      title="Delete client"
                      className="h-8 w-8 p-0 text-slate-300 hover:text-rose-600 shrink-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>

                  {pc && (
                    <div className="text-xs text-slate-600 border-t border-slate-100 pt-2.5">
                      <div className="flex items-center gap-1.5 text-slate-700"><User size={11} className="text-slate-400" /> <span className="font-medium">{pc.first_name} {pc.last_name}</span></div>
                      {pc.mobile_phone && <div className="flex items-center gap-1.5 mt-1 text-slate-500 font-mono"><Phone size={11} className="text-slate-400" /> {pc.mobile_phone}</div>}
                      {pc.email && <div className="flex items-center gap-1.5 mt-1 text-slate-500 truncate"><Mail size={11} className="text-slate-400 shrink-0" /> <span className="truncate">{pc.email}</span></div>}
                    </div>
                  )}

                  {addr && (
                    <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
                      <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
                      <span className="truncate">{addr.line1}, {addr.suburb} {addr.state} {addr.postcode}</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                    <div className="flex-1 flex items-center gap-1.5 text-xs">
                      <FileText size={12} className="text-cyan-600" />
                      <span className="font-mono font-semibold text-slate-900">{c.quote_count || 0}</span>
                      <span className="text-slate-500">quotes</span>
                      {c.active_quote_count > 0 && <Badge variant="outline" className="ml-1 rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-800">{c.active_quote_count} live</Badge>}
                    </div>
                    <div className="flex-1 flex items-center gap-1.5 text-xs">
                      <Briefcase size={12} className="text-emerald-600" />
                      <span className="font-mono font-semibold text-slate-900">{c.job_count || 0}</span>
                      <span className="text-slate-500">jobs</span>
                      {c.active_job_count > 0 && <Badge variant="outline" className="ml-1 rounded-sm text-[9px] border-emerald-300 bg-emerald-50 text-emerald-800">{c.active_job_count} active</Badge>}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
          {clients.length === 0 && !isFetching && (
            <Card className="rounded-sm p-12 industrial-shadow col-span-full text-center">
              <Building2 className="text-slate-300 mx-auto mb-3" size={32} />
              <div className="font-display font-semibold text-slate-700 mb-1">No clients match your search</div>
              <div className="text-xs text-slate-500 mb-4">Start with a new enquiry to create your first client record.</div>
              <Button onClick={() => navigate("/enquiry/new")} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm">
                <Plus size={14} className="mr-1.5" /> New Enquiry
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="delete-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-500" /> Delete {delTarget?.name}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>This permanently deletes the client and <b>everything linked to them</b>:</p>
            <div className="border border-rose-200 bg-rose-50 rounded-sm p-3 text-xs space-y-1">
              <div>• All contacts &amp; addresses</div>
              <div>• {delTarget?.quote_count || 0} quote(s) — including attachments</div>
              <div>• {delTarget?.job_count || 0} job(s), plus any deals, bookings &amp; tasks</div>
            </div>
            <p className="text-xs text-slate-400">This cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-sm">Cancel</Button>
            <Button
              data-testid="confirm-delete-client-btn"
              onClick={() => deleteClient.mutate(delTarget.id)}
              disabled={deleteClient.isPending}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleteClient.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
